import { useEffect, useState } from 'react';
import type { Embedding, ThemeEmbeddings } from './search.types';
import { addError } from '../logger';

export const EMBEDDINGS_STORE = 'https://withjoy.com/media/raw/paper-embeddings';

type PrecomputedEmbeddings = Readonly<{
  embeddingSizeBytes: number;
  keywords: string[];
  keywordsWellKnown: {
    beige: number;
    foil: number;
    free: number;
    landscape: number;
    photo: number;
    portrait: number;
    qrCode: number;
    white: number;
  };
  themes: Record<string, Readonly<[ReadonlyArray<number>, Readonly<[number, number, number]> | [], ReadonlyArray<number>]>>;
}>;

type PrecomputedEmbeddingsResult = Readonly<{
  themes: Map<string, ThemeEmbeddings> | undefined;
  embeddingMap: Map<string, Embedding>;
  wellKnownEmbeddings: Readonly<{
    [K in keyof PrecomputedEmbeddings['keywordsWellKnown']]: Embedding;
  }>;
}>;

let precomputedEmbeddingsPromise: Promise<PrecomputedEmbeddingsResult> | undefined;

/**
 * Color keywords in the bundle are namespaced (`color: roseGold`) to keep them
 * apart from style keywords. Returns the bare color name, or `''` for a keyword
 * that isn't a color (a theme's color slot may hold `colorful`).
 */
const COLOR_KEYWORD_PREFIX = 'color: ';
function colorKeywordName(keyword: string | undefined): string {
  return keyword?.startsWith(COLOR_KEYWORD_PREFIX) ? keyword.slice(COLOR_KEYWORD_PREFIX.length) : '';
}

export function usePrecomputedEmbeddings(): PrecomputedEmbeddingsResult {
  const [themesAndEmbeddings, setThemesAndEmbeddings] = useState<PrecomputedEmbeddingsResult>({
    themes: undefined,
    embeddingMap: new Map<string, Embedding>(),
    wellKnownEmbeddings: {
      beige: [],
      foil: [],
      free: [],
      landscape: [],
      photo: [],
      portrait: [],
      qrCode: [],
      white: [],
    },
  });

  useEffect(() => {
    void (async () => {
      // PrecomputedEmbeddings are global to Joy. Only load once per app-instance.
      precomputedEmbeddingsPromise =
        precomputedEmbeddingsPromise ??
        (async () => {
          const [bundle, bigBuffer] = (await Promise.all([
            fetch(`${EMBEDDINGS_STORE}/themes.json`).then(r => r.json()),
            fetch(`${EMBEDDINGS_STORE}/themes.bin`).then(r => r.arrayBuffer()),
          ])) as [PrecomputedEmbeddings, ArrayBuffer];

          const embeddings = bundle.keywords.map((_, i) => {
            const keywordBuffer = bigBuffer.slice(i * bundle.embeddingSizeBytes, (i + 1) * bundle.embeddingSizeBytes);
            return Array.from(new Float32Array(keywordBuffer)) as Embedding;
          });

          const wellKnownEmbeddings = {
            beige: embeddings[bundle.keywordsWellKnown.beige],
            foil: embeddings[bundle.keywordsWellKnown.foil],
            free: embeddings[bundle.keywordsWellKnown.free],
            landscape: embeddings[bundle.keywordsWellKnown.landscape],
            photo: embeddings[bundle.keywordsWellKnown.photo],
            portrait: embeddings[bundle.keywordsWellKnown.portrait],
            qrCode: embeddings[bundle.keywordsWellKnown.qrCode],
            white: embeddings[bundle.keywordsWellKnown.white],
          };

          const themes = new Map(
            // Object.entries is >95% the compat table is being updated soon.
            // eslint-disable-next-line compat/compat
            Object.entries(bundle.themes).map(([id, [conceptIndexes, colorIndexes, customIndexes]]) => {
              return [
                id,
                {
                  concepts: conceptIndexes.map(i => embeddings[i]),
                  conceptNames: conceptIndexes.map(i => bundle.keywords[i]),
                  colors: colorIndexes.length === 0 ? ([] as []) : (colorIndexes.map(i => embeddings[i]) as Readonly<[Embedding, Embedding, Embedding]> | []),
                  colorNames: colorIndexes.map(i => colorKeywordName(bundle.keywords[i])),
                  customs: customIndexes.map(i => embeddings[i]),
                },
              ];
            }),
          );

          return {
            themes,
            embeddingMap: new Map(bundle.keywords.map((keyword, i) => [keyword, embeddings[i]])),
            wellKnownEmbeddings,
          };
        })();

      try {
        setThemesAndEmbeddings(await precomputedEmbeddingsPromise);
      } catch (error) {
        // Not easy to rethrow here, so using addError instead.
        addError(error, { context: 'usePrecomputedEmbeddings' });
        precomputedEmbeddingsPromise = undefined; // Prevent re-awaiting a rejected promise on next mount.
      }
    })();
  }, []);

  return themesAndEmbeddings;
}
